// src/components/chat/NewChatModal.jsx
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import { createDirect, createGroup } from '../../services/chatApi';


const CSS = `
.ncm-tabs {
    display: inline-flex; gap: 4px; padding: 3px;
    background: var(--c-input-bg);
    border: 1px solid var(--c-border);
    border-radius: 999px;
    margin-bottom: 14px;
}
.ncm-tab {
    background: transparent; border: none;
    padding: 6px 16px; border-radius: 999px;
    font-size: 13px; font-weight: 600;
    color: var(--c-text-3);
    cursor: pointer; font-family: inherit;
    transition: all .15s;
}
.ncm-tab:hover { color: var(--c-text-1); }
.ncm-tab.active { background: var(--brand-700, #0a4aa8); color: #fff; }

.ncm-search {
    width: 100%; padding: 9px 12px; font-size: 14px;
    border: 1px solid var(--c-border); border-radius: 8px;
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none;
    margin-bottom: 14px;
    transition: border-color .15s, box-shadow .15s;
}
.ncm-search:focus {
    border-color: var(--brand-700, #0a4aa8);
    box-shadow: 0 0 0 3px rgba(10, 74, 168, .12);
}

.ncm-list { max-height: 320px; overflow-y: auto; display: flex; flex-direction: column; gap: 4px; }
.ncm-row {
    padding: 10px 12px; border-radius: 8px;
    cursor: pointer;
    display: flex; justify-content: space-between; align-items: center; gap: 8px;
    transition: background .15s;
}
.ncm-row:hover { background: var(--c-msg-bg); }
.ncm-row-name { font-weight: 600; font-size: 13px; color: var(--c-text-1); }
.ncm-row-hint { font-size: 11.5px; color: var(--c-text-4); margin-top: 2px; }
.ncm-role-badge {
    background: var(--c-msg-bg); color: var(--c-text-3);
    border: 1px solid var(--c-border);
    border-radius: 6px; padding: 2px 9px;
    font-size: 10.5px; font-weight: 600;
    letter-spacing: 0.3px;
}

.ncm-empty { color: var(--c-text-4); font-size: 13px; padding: 16px 4px; text-align: center; }

.ncm-label { font-size: 13px; font-weight: 600; color: var(--c-text-2); display: block; margin-bottom: 6px; }
.ncm-input, .ncm-textarea {
    width: 100%; padding: 9px 12px; font-size: 14px;
    border: 1px solid var(--c-border); border-radius: 8px;
    background: var(--c-input-bg); color: var(--c-text-1);
    font-family: inherit; outline: none;
    transition: border-color .15s, box-shadow .15s;
}
.ncm-textarea { resize: vertical; min-height: 60px; }
.ncm-input:focus, .ncm-textarea:focus {
    border-color: var(--brand-700, #0a4aa8);
    box-shadow: 0 0 0 3px rgba(10, 74, 168, .12);
}

.ncm-picks {
    max-height: 220px; overflow-y: auto;
    border: 1px solid var(--c-border); border-radius: 8px;
    padding: 10px;
    background: var(--c-input-bg);
}
.ncm-pick {
    display: flex; align-items: center; gap: 8px;
    padding: 5px 4px;
    cursor: pointer; font-size: 13px;
}
.ncm-pick input { accent-color: var(--brand-700, #0a4aa8); }
.ncm-pick-hint { font-size: 11px; color: var(--c-text-4); }

.ncm-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid transparent; border-radius: 8px;
    padding: 8px 14px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: all .15s;
}
.ncm-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ncm-btn-primary { background: var(--brand-700, #0a4aa8); color: #fff; border-color: var(--brand-700, #0a4aa8); }
.ncm-btn-primary:hover:not(:disabled) { background: var(--brand-800, #07357f); border-color: var(--brand-800, #07357f); }
.ncm-btn-ghost { background: transparent; color: var(--c-text-2); border-color: var(--c-border); }
.ncm-btn-ghost:hover:not(:disabled) { background: var(--c-msg-bg); }
`;


export default function NewChatModal({ show, onClose, onCreated }) {
    const { user } = useContext(AuthContext);
    const [tab, setTab]         = useState('direct');
    const [people, setPeople]   = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter]   = useState('');

    const [groupName, setGroupName]   = useState('');
    const [groupDesc, setGroupDesc]   = useState('');
    const [groupPicks, setGroupPicks] = useState([]);

    const canGroup = user?.role === 'leader' || user?.role === 'admin';

    useEffect(() => {
        if (!show) return;
        setLoading(true);
        api.get('/chats/candidates')
            .then(({ data }) => setPeople(data || []))
            .catch(() => setPeople([]))
            .finally(() => setLoading(false));
    }, [show]);

    const visiblePeople = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return people;
        return people.filter(p =>
            (p.name || '').toLowerCase().includes(q) ||
            (p.email || '').toLowerCase().includes(q)
        );
    }, [people, filter]);

    const resetAndClose = () => {
        setTab('direct'); setFilter(''); setGroupName(''); setGroupDesc(''); setGroupPicks([]);
        onClose();
    };

    const startDirect = async (userId) => {
        try {
            const chat = await createDirect(userId);
            onCreated?.(chat);
            resetAndClose();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo iniciar el chat');
        }
    };

    const startGroup = async () => {
        if (!groupName.trim()) { toast.warn('El nombre es obligatorio'); return; }
        if (!groupPicks.length) { toast.warn('Selecciona al menos un participante'); return; }
        try {
            const chat = await createGroup({ name: groupName, description: groupDesc, participants: groupPicks });
            onCreated?.(chat);
            resetAndClose();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo crear el grupo');
        }
    };

    const togglePick = (id) => {
        setGroupPicks(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    return (
        <>
            <style>{CSS}</style>
            <Modal show={show} onHide={resetAndClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>Nuevo chat</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="ncm-tabs">
                        <button type="button" className={`ncm-tab${tab === 'direct' ? ' active' : ''}`} onClick={() => setTab('direct')}>Directo</button>
                        {canGroup && (
                            <button type="button" className={`ncm-tab${tab === 'group' ? ' active' : ''}`} onClick={() => setTab('group')}>Grupo</button>
                        )}
                    </div>

                    <input
                        type="text"
                        className="ncm-search"
                        placeholder="Buscar persona…"
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                    />

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 32 }}><Spinner animation="border" size="sm" /></div>
                    ) : visiblePeople.length === 0 ? (
                        <p className="ncm-empty">
                            {filter.trim()
                                ? 'Sin coincidencias para tu búsqueda.'
                                : user?.role === 'admin'
                                    ? 'No hay otros usuarios staff activos.'
                                    : 'No hay compañeros disponibles en tus departamentos.'}
                        </p>
                    ) : tab === 'direct' ? (
                        <div className="ncm-list">
                            {visiblePeople.map(p => {
                                const deptNames = (p.departments || []).map(d => d?.name || d).filter(Boolean).join(' · ');
                                return (
                                    <div key={p._id} onClick={() => startDirect(p._id)} className="ncm-row">
                                        <div>
                                            <div className="ncm-row-name">{p.name}</div>
                                            <div className="ncm-row-hint">{p.email || deptNames}</div>
                                        </div>
                                        <span className="ncm-role-badge">{p.role}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: 12 }}>
                                <label className="ncm-label">Nombre del grupo</label>
                                <input type="text" className="ncm-input" value={groupName} onChange={e => setGroupName(e.target.value)} />
                            </div>
                            <div style={{ marginBottom: 14 }}>
                                <label className="ncm-label">Descripción (opcional)</label>
                                <textarea rows={2} className="ncm-textarea" value={groupDesc} onChange={e => setGroupDesc(e.target.value)} />
                            </div>
                            <label className="ncm-label">
                                Participantes
                                <span style={{ fontWeight: 400, color: 'var(--c-text-4)', marginLeft: 6 }}>({groupPicks.length})</span>
                            </label>
                            <div className="ncm-picks">
                                {visiblePeople.map(p => {
                                    const hint = p.email || (p.departments || []).map(d => d?.name || d).filter(Boolean).join(' · ');
                                    return (
                                        <label key={p._id} className="ncm-pick">
                                            <input
                                                type="checkbox"
                                                checked={groupPicks.includes(p._id)}
                                                onChange={() => togglePick(p._id)}
                                            />
                                            <span>{p.name}</span>
                                            {hint && <span className="ncm-pick-hint">· {hint}</span>}
                                        </label>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="ncm-btn ncm-btn-ghost" onClick={resetAndClose}>Cancelar</button>
                    {tab === 'group' && canGroup && (
                        <button type="button" className="ncm-btn ncm-btn-primary" onClick={startGroup}>Crear grupo</button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
}
